'use client';
import step1 from '@/assets/images/how-to-make/STEP1.webp';
import step2 from '@/assets/images/how-to-make/STEP2.webp';
import step3 from '@/assets/images/how-to-make/STEP3.webp';
import step4 from '@/assets/images/how-to-make/STEP4.webp';
import step5 from '@/assets/images/how-to-make/STEP5.webp';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import { BREAKPOINT } from '@/constants';
import { anton } from '@/lib/font';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import styles from './HowToMakeSlider.module.scss';
import '@splidejs/react-splide/css';

function HowToMakeSlider() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSp, setIsSp] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const splideRef = useRef<Splide>(null);

  /**
   * NOTE: 画像の拡張子をwebpに変更する
   * next/imageはフォールバックがないのでwebp非対応のブラウザでは表示できないがシェア的に問題ないのでこのままにする
   * 万が一お問い合わせが来たらその時再検討する
   */
  const images = [
    {
      id: 1,
      src: step1.src,
      alt: 'lineUp',
      type: 'lineUp',
      title: 'LINE UP',
      splideTextArea: (
        <div className={styles.splideTextArea}>
          <div className={styles.splideTitle}>作品をえらぶ</div>
          <div className={styles.splideDescription}>歴代人気作品の中から、グッズにしたい作品をえらびます。</div>
        </div>
      ),
    },
    {
      id: 2,
      src: step2.src,
      alt: 'material',
      type: 'material',
      title: 'MANGA',
      splideTextArea: (
        <div className={styles.splideTextArea}>
          <div className={styles.splideTitle}>コマをえらぶ</div>
          <div className={styles.splideDescription}>作品の中から、お気に入りのコマをえらびます。</div>
        </div>
      ),
    },
    {
      id: 3,
      src: step3.src,
      alt: 'item',
      type: 'item',
      title: 'ITEM',
      splideTextArea: (
        <div className={styles.splideTextArea}>
          <div className={styles.splideTitle}>アイテムをえらぶ</div>
          <div className={styles.splideDescription}>
            Tシャツ、トートバッグなどから、グッズ化したいアイテムをえらびます。
          </div>
        </div>
      ),
    },
    {
      id: 4,
      src: step4.src,
      alt: 'design',
      type: 'design',
      title: 'DESIGN',
      splideTextArea: (
        <div className={styles.splideTextArea}>
          <div className={styles.splideTitle}>コマを配置する</div>
          <div className={styles.splideDescription}>コマを自由に追加・配置してオリジナルデザインに仕上げます。</div>
          <div className={styles.splideSubDescription}>※デザインが固定のアイテムもございます。</div>
        </div>
      ),
    },
    {
      id: 5,
      src: step5.src,
      alt: 'finish',
      type: 'finish',
      title: 'COMPLETE',
      splideTextArea: (
        <div className={styles.splideTextArea}>
          <div className={styles.splideTitle}>完成！</div>
          <div className={styles.splideDescription}>
            できあがったデザインを、そのまま注文。あなただけのグッズが届きます。
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const checkViewport = () => {
      const isSpView = window.innerWidth < BREAKPOINT.SP;
      setIsSp(isSpView);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => {
      window.removeEventListener('resize', checkViewport);
    };
  }, []);

  const handlePrevSlide = () => {
    if (splideRef.current) {
      splideRef.current.go('<');
      checkSlideState();
    }
  };

  const handleNextSlide = () => {
    if (splideRef.current) {
      splideRef.current.go('>');
      checkSlideState();
    }
  };

  const checkSlideState = () => {
    if (splideRef.current?.splide) {
      const currentIndex = splideRef.current.splide.index;
      const lastIndex = images.length - 1;
      setIsFirstSlide(currentIndex === 0);
      setIsLastSlide(currentIndex === lastIndex);
    }
  };

  const handleSlideClick = (index: number) => {
    setActiveIndex(index);
    splideRef.current?.go(index);
    checkSlideState();
  };

  const spOptions = {
    autoplay: false,
    type: 'slide',
    arrows: false,
    pagination: false,
    pauseOnHover: false,
    perPage: 1,
  };

  const pcOptions = {
    ...spOptions,
    perPage: 5,
    autoplay: false,
    drag: false,
    clones: 0,
    waitForTransition: false,
    speed: 0,
    gap: 16,
  };
  const splideOptions = isSp ? spOptions : pcOptions;

  return (
    <div className={styles.splideContainer}>
      {isSp && (
        <>
          <button type='button' className={styles.customPrevArrow} onClick={handlePrevSlide} disabled={isFirstSlide}>
            <RoundArrowIcon direction='left' color='black' size='large' isBold={true} />
          </button>
          <button type='button' className={styles.customNextArrow} onClick={handleNextSlide} disabled={isLastSlide}>
            <RoundArrowIcon direction='right' color='black' size='large' isBold={true} />
          </button>
        </>
      )}
      <Splide
        key={isSp ? 'sp' : 'pc'}
        className={styles.splide}
        options={splideOptions}
        onMove={({ index }) => {
          setActiveIndex(index);
          checkSlideState();
        }}
        aria-label='オリジナルグッズのつくりかた'
        hasTrack={false}
        ref={splideRef}
      >
        <SplideTrack className={styles.splide}>
          {images.map((image) => (
            <SplideSlide key={image.id} className={styles.splideSlide}>
              <div className={styles.splideSlideContent}>
                <div className={`${anton.className} ${styles.splideImageNumber}`}>{image.id}</div>
                <div className={`${anton.className} ${styles.splideImageTitle} ${styles[`${image.type}TitleColor`]}`}>
                  {image.title}
                </div>
                <div className={styles.splideImageWrapper}>
                  <Image
                    src={image.src}
                    alt={image.alt}
                    width={360}
                    height={360}
                    unoptimized
                    style={{ objectFit: 'contain', height: 'auto', width: '100%' }}
                  />
                </div>
                {image.splideTextArea}
              </div>
            </SplideSlide>
          ))}
        </SplideTrack>
      </Splide>
      <div className={styles.buttonNavigation}>
        {images.map((image, index) => (
          <button
            key={image.id}
            onClick={() => handleSlideClick(index)}
            className={`${styles.navButton} ${activeIndex === index ? styles.active : ''}`}
            type='button'
          >
            <span className={anton.className}>{image.id}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default HowToMakeSlider;
